/**
 * @format
 */
import { StackItemNode, StyleProperty, StylingStatus, ToolType } from "../types/editor-types";
import { BaseToolService } from "./base-tool.service";

export class BoldUnderlineBaseService extends BaseToolService {
   protected _START_ID = "START-STYLE";
   protected _END_ID = "END-STYLE";

   readonly toolType: ToolType;

   constructor(toolType: ToolType) {
      super();
      this.toolType = toolType;
   }

   private checkIs: Record<ToolType, (node: Node) => boolean> = {
      BOLD: this.isB,
      UNDERLINE: this.isU,
   };

   private tagNameIs: Record<ToolType, (name: string) => boolean> = {
      BOLD: (tagName: string) => tagName === "b" || tagName === "strong",
      UNDERLINE: (tagName: string) => tagName === "u",
   };

   protected insertNodeInCurrentRange(
      currentRange: Range,
      wrapperNode: Element,
      isParameter: boolean,
      undoStyleType: boolean,
      templateContextElement: Element,
      setTemplateContext: (templateContext: Element) => void
   ): void {
      const nodeList = Array.from(wrapperNode.childNodes);
      const { commonAncestorContainer } = currentRange;

      // When the selection IS a TEMPLATE_CONTEXT element
      if (isParameter && !undoStyleType) {
         const templateElement = templateContextElement;

         if (Boolean(templateElement.previousSibling)) {
            const parentNode = templateElement.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(templateElement);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [templateElement, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            setTemplateContext(<Element>nodeList[0].firstChild);
            return;
         }

         if (this.isNodeTypeChildWithoutSibling(templateElement)) {
            const parentNode = templateElement.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(templateElement.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [templateElement.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            setTemplateContext(<Element>nodeList[0].firstChild);
            return;
         }

         const parentNode = templateElement.parentNode;
         parentNode.removeChild(templateElement);

         while (parentNode.firstChild) {
            nodeList.push(parentNode.firstChild);
            parentNode.removeChild(parentNode.firstChild);
         }

         this.appendAllChildren(parentNode, nodeList);
         setTemplateContext(<Element>nodeList[0].firstChild);
         return;
      }

      if (isParameter && undoStyleType) {
         const templateElement = templateContextElement;
         const commonAncestorBold = this.getParentNode(templateElement);
         if (Boolean(commonAncestorBold.previousSibling)) {
            const parentNode = commonAncestorBold.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorBold);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorBold, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            setTemplateContext(<Element>nodeList[0].firstChild);
            return;
         }

         if (this.isNodeTypeChildWithoutSibling(commonAncestorBold)) {
            const parentNode = commonAncestorBold.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorBold.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorBold.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
            setTemplateContext(<Element>nodeList[0].firstChild);
            return;
         }

         const parentNode = commonAncestorBold.parentNode;
         parentNode.removeChild(commonAncestorBold);

         while (parentNode.firstChild) {
            nodeList.push(parentNode.firstChild);
            parentNode.removeChild(parentNode.firstChild);
         }

         this.appendAllChildren(parentNode, nodeList);
         setTemplateContext(<Element>nodeList[0].firstChild);
         return;
      }

      // When the selection IS a TEXT element
      if (this.isText(commonAncestorContainer) && !undoStyleType) {
         if (Boolean(commonAncestorContainer.previousSibling)) {
            const parentNode = commonAncestorContainer.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorContainer);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorContainer, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else if (this.isNodeTypeChildWithoutSibling(commonAncestorContainer)) {
            const parentNode = commonAncestorContainer.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorContainer.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorContainer.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else {
            const parentNode = commonAncestorContainer.parentNode;
            parentNode.removeChild(commonAncestorContainer);

            while (parentNode.firstChild) {
               nodeList.push(parentNode.firstChild);
               parentNode.removeChild(parentNode.firstChild);
            }

            this.appendAllChildren(parentNode, nodeList);
         }

         return;
      }

      if (this.isText(commonAncestorContainer) && undoStyleType) {
         const commonAncestorBold = this.getParentNode(commonAncestorContainer);

         if (Boolean(commonAncestorBold.previousSibling)) {
            const parentNode = commonAncestorBold.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorBold);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorBold, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else if (this.isNodeTypeChildWithoutSibling(commonAncestorBold)) {
            const parentNode = commonAncestorBold.parentNode.parentNode;
            const brotherNodes = this.getAllNextSiblingNodesFromNode(commonAncestorBold.parentNode);
            nodeList.push(...brotherNodes);
            this.removeAllChildFromParent(parentNode, [commonAncestorBold.parentNode, ...brotherNodes]);
            this.appendAllChildren(parentNode, nodeList);
         } else {
            const parentNode = commonAncestorBold.parentNode;
            parentNode.removeChild(commonAncestorBold);

            while (parentNode.firstChild) {
               nodeList.push(parentNode.firstChild);
               parentNode.removeChild(parentNode.firstChild);
            }

            this.appendAllChildren(parentNode, nodeList);
         }

         return;
      }
      // When the selection is not a TEXT element and TEMPLATE CONTEXT (parameter)
      (<Element>currentRange.commonAncestorContainer).innerHTML = wrapperNode.innerHTML;
      return;
   }

   protected getBoldStyledElementsAsString(
      range: Range,
      isParameter: boolean,
      templateContext: Element,
      undoStyleType
   ): string {
      // to prevent selection issue due to double click over template-context parameter
      let firstNode: Node = range.commonAncestorContainer.firstChild;

      if (isParameter) {
         return this.getParameterStyled(templateContext, undoStyleType);
      }

      if (this.isSelectionInlineText(range) && !undoStyleType) {
         return this.getStartEndStyle(range);
      }

      if (this.isSelectionInlineText(range) && undoStyleType) {
         firstNode = this.getParentNode(range.commonAncestorContainer);
      }

      return this.getStyledChildNodeAsString(firstNode, range, undoStyleType, this.isSelectionInlineText(range));
   }

   protected getStyledChildNodeAsString(
      firstNode: Node,
      range: Range,
      undoStyleType: boolean,
      isSelectionInlineText: boolean
   ): string {
      if (isSelectionInlineText && undoStyleType) {
         return this.undoRecursiveForInlineSelection(firstNode, range, "KEEP-STYLE", []);
      } else if (undoStyleType) {
         return this.undoRecursiveStyle(firstNode, range, "KEEP-STYLE", []);
      } else {
         return this.getRecursiveStyle(firstNode, range, "KEEP-STYLE");
      }
   }

   protected undoRecursiveForInlineSelection(
      currentNode: Node,
      range: Range,
      status: StylingStatus,
      stackOfNodes: Array<StackItemNode>
   ): string {
      if (!Boolean(currentNode)) {
         return "";
      }

      const stack = [...stackOfNodes];

      if (!this.isText(currentNode)) {
         stack.push(new StackItemNode(currentNode));
      }

      if (currentNode === range.startContainer) {
         return this.undoBoldStartEndStyle(range, stack);
      }

      if (currentNode.contains(range.startContainer)) {
         if (this.checkIs[this.toolType](currentNode)) {
            return (
               this.getStartNodeTagAsString(currentNode) +
               this.undoRecursiveForInlineSelection(currentNode.firstChild, range, status, stack) +
               this.getEndNodeTagAsString(currentNode)
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.undoRecursiveForInlineSelection(currentNode.firstChild, range, status, stack) +
            this.getEndNodeTagAsString(currentNode) +
            this.undoRecursiveForInlineSelection(currentNode.nextSibling, range, status, stack)
         );
      }

      if (this.isText(currentNode)) {
         return (
            currentNode.nodeValue + this.undoRecursiveForInlineSelection(currentNode.nextSibling, range, status, stack)
         );
      }

      return (
         (<Element>currentNode).outerHTML +
         this.undoRecursiveForInlineSelection(currentNode.nextSibling, range, status, stack)
      );
   }

   protected undoRecursiveStyle(
      currentNode: Node,
      range: Range,
      status: StylingStatus,
      stackOfNodes: Array<StackItemNode>
   ): string {
      if (!Boolean(currentNode)) {
         return "";
      }

      const stack = [...stackOfNodes];
      if (this.checkIs[this.toolType](currentNode) || (stack.length > 0 && !this.isText(currentNode))) {
         stack.push(new StackItemNode(currentNode));
      }

      if (currentNode === range.startContainer) {
         return (
            this.getStartStyle(currentNode, range.startOffset, true, stack) +
            this.undoRecursiveStyle(currentNode.nextSibling, range, "STYLING", stack)
         );
      }

      if (currentNode === range.endContainer) {
         return (
            this.getEndStyle(currentNode, range.endOffset, true, stack) +
            this.undoRecursiveStyle(currentNode.nextSibling, range, "KEEP-STYLE", stack)
         );
      }

      if (currentNode.contains(range.startContainer)) {
         if (this.checkIs[this.toolType](currentNode)) {
            return (
               this.getStartNodeTagAsString(currentNode) +
               this.undoRecursiveStyle(currentNode.firstChild, range, status, stack) +
               this.undoRecursiveStyle(currentNode.nextSibling, range, "NOT-STYLING", [])
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.firstChild, range, status, stack) +
            this.getEndNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.nextSibling, range, "NOT-STYLING", stack)
         );
      }

      if (currentNode.contains(range.endContainer)) {
         if (this.checkIs[this.toolType](currentNode)) {
            return (
               this.undoRecursiveStyle(currentNode.firstChild, range, status, stack) +
               this.getEndNodeTagAsString(currentNode) +
               this.undoRecursiveStyle(currentNode.nextSibling, range, "KEEP-STYLE", [])
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.firstChild, range, status, stack) +
            this.getEndNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.nextSibling, range, "KEEP-STYLE", stack)
         );
      }

      if (status === "NOT-STYLING") {
         if (this.isText(currentNode)) {
            return currentNode.nodeValue + this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack);
         }

         if (this.checkIs[this.toolType](currentNode)) {
            return (
               (<Element>currentNode).innerHTML + this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack)
            );
         }

         if (this.isTemplateContext(currentNode)) {
            return (
               (<Element>currentNode).outerHTML + this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack)
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.firstChild, range, status, stack) +
            this.getEndNodeTagAsString(currentNode) +
            this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack)
         );
      }

      if (this.isText(currentNode)) {
         return currentNode.nodeValue + this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack);
      }

      return (<Element>currentNode).outerHTML + this.undoRecursiveStyle(currentNode.nextSibling, range, status, stack);
   }

   protected getRecursiveStyle(currentNode: Node, range: Range, status: StylingStatus): string {
      if (!Boolean(currentNode)) {
         return "";
      }

      if (currentNode === range.startContainer) {
         return (
            this.getStartStyle(currentNode, range.startOffset, false) +
            this.getRecursiveStyle(currentNode.nextSibling, range, "STYLING")
         );
      }

      if (currentNode === range.endContainer) {
         return (
            this.getEndStyle(currentNode, range.endOffset, false) +
            this.getRecursiveStyle(currentNode.nextSibling, range, "KEEP-STYLE")
         );
      }

      if (currentNode.contains(range.startContainer)) {
         if (this.hasSiblingSameStyle(currentNode, "color")) {
            return "";
         }
         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.nextSibling, range, "STYLING")
         );
      }

      if (currentNode.contains(range.endContainer)) {
         if (this.hasSiblingSameStyle(currentNode, "color")) {
            return "";
         }
         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.nextSibling, range, "NOT-STYLING")
         );
      }

      if (status === "STYLING") {
         if (this.isText(currentNode)) {
            return `<b>${currentNode.nodeValue}</b>` + this.getRecursiveStyle(currentNode.nextSibling, range, status);
         }

         if (this.isTemplateContext(currentNode)) {
            return (
               `<b>${(<Element>currentNode).outerHTML}</b>` +
               this.getRecursiveStyle(currentNode.nextSibling, range, status)
            );
         }

         return (
            this.getStartNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.firstChild, range, status) +
            this.getEndNodeTagAsString(currentNode) +
            this.getRecursiveStyle(currentNode.nextSibling, range, status)
         );
      }

      if (this.isText(currentNode)) {
         return currentNode.nodeValue + this.getRecursiveStyle(currentNode.nextSibling, range, status);
      }

      return (<Element>currentNode).outerHTML + this.getRecursiveStyle(currentNode.nextSibling, range, status);
   }

   protected getParameterStyled(parameterElement: Element, undoStyleType: boolean): string {
      if (!undoStyleType) {
         return this.toolType === "BOLD"
            ? `<b id="${this._START_ID}">${parameterElement.outerHTML}</b>`
            : `<u id="${this._START_ID}">${parameterElement.outerHTML}</u>`;
      } else {
         let currentNode = parameterElement.parentNode;
         while (!this.checkIs[this.toolType](currentNode)) {
            currentNode = currentNode.parentNode;
         }

         return (<Element>currentNode).innerHTML;
      }
   }

   protected getStartStyle(node: Node, offset: number, undoStyleType: boolean, stack?: Array<StackItemNode>): string {
      if (this.isText(node) && !undoStyleType) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);

         return this.toolType === "BOLD"
            ? `${part1}<b id="${this._START_ID}">${part2}</b>`
            : `${part1}<u id="${this._START_ID}">${part2}</u>`;
      } else if (this.isText(node) && Boolean(stack)) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);
         const result = this.getStartBoundaryFromStack(stack, this._START_ID);

         return `${part1}${result}${part2}`;
      } else {
         return this.toolType === "BOLD"
            ? `<b id="${this._START_ID}">${(<Element>node).outerHTML}</b>`
            : `<u id="${this._START_ID}">${(<Element>node).outerHTML}</u>`;
      }
   }

   protected getEndStyle(node: Node, offset: number, undoStyleType: boolean, stack?: Array<StackItemNode>): string {
      if (this.isText(node) && !undoStyleType) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);

         return this.toolType === "BOLD"
            ? `<b id="${this._END_ID}">${part1}</b>${part2}`
            : `<u id="${this._END_ID}">${part1}</u>${part2}`;
      } else if (this.isText(node)) {
         const value = node.nodeValue;
         const part1 = value.slice(0, offset);
         const part2 = value.slice(offset);
         const result = this.getEndBoundaryFromStack(stack, this._END_ID);

         return `${part1}${result}${part2}`;
      } else {
         return this.toolType === "BOLD"
            ? `<b id="${this._END_ID}">${(<Element>node).outerHTML}</b>`
            : `<u id="${this._END_ID}">${(<Element>node).outerHTML}</u>`;
      }
   }

   protected getStartEndStyle(range: Range): string {
      const nodeValue = range.startContainer.nodeValue;
      const part1 = nodeValue.slice(0, range.startOffset);
      const part2 = nodeValue.slice(range.startOffset, range.endOffset);
      const part3 = nodeValue.slice(range.endOffset);

      if (this.isNodeTypeChildWithoutSibling(range.startContainer) && part1 === "" && part3 === "") {
         return this.getStyledElementWithId(range.startContainer.parentNode, this._START_ID);
      }

      return this.toolType === "BOLD"
         ? `${part1}<b id="${this._START_ID}">${part2}</b>${part3}`
         : `${part1}<u id="${this._START_ID}">${part2}</u>${part3}`;
   }

   protected undoBoldStartEndStyle(range: Range, stack: Array<StackItemNode>): string {
      const nodeValue = range.startContainer.nodeValue;

      const part1 = nodeValue.slice(0, range.startOffset);
      const part2 = nodeValue.slice(range.startOffset, range.endOffset);
      const part3 = nodeValue.slice(range.endOffset);

      const startBoundary = this.getStartBoundaryFromStack(stack, this._START_ID);
      const endBoundary = this.getEndBoundaryFromStack(stack, this._END_ID);

      return `${part1}${startBoundary}${part2}${endBoundary}${part3}`;
   }

   protected getStyledElementWithId(parentNode: ParentNode, elementId: string): string {
      const htmlElement = <HTMLElement>parentNode;
      htmlElement.id = elementId;

      return htmlElement.outerHTML;
   }

   protected hasSiblingSameStyle(node: Node, property: StyleProperty) {
      if (!Boolean(node.nextSibling)) {
         return false;
      }

      if (this.isText(node.nextSibling)) {
         return false;
      }

      const siblingElement = <HTMLElement>node.nextSibling;

      return (
         siblingElement.style[0] === "display" &&
         siblingElement.style[1] === property &&
         siblingElement[2] === undefined
      );
   }

   protected getParentNode(node: Node): Node {
      if (!this.isText(node) && Boolean((<Element>node).attributes["ng-model"])) {
         throw Error("Parent bold node NOT FOUND");
      }

      if (this.checkIs[this.toolType](node)) {
         return node;
      }

      return this.getParentNode(node.parentNode);
   }

   protected getStartBoundaryFromStack(stackOfParentElements: Array<StackItemNode>, startId: string): string {
      const isLastItem = (index: number) => {
         const limit = index + 1;
         return stackOfParentElements.length === limit;
      };

      return stackOfParentElements.reduce((previousValue, currentValue, currentIndex) => {
         const tagName = currentValue.nodeName;
         if (this.tagNameIs[this.toolType](tagName)) {
            return `</${tagName}>`;
         }

         if (currentValue.style !== "" && isLastItem(currentIndex)) {
            return `</${tagName}>${previousValue}<${tagName} style="${currentValue.style}" id="${startId}">`;
         }

         if (currentValue.style !== "") {
            return `</${tagName}>${previousValue}<${tagName} style="${currentValue.style}">`;
         }

         if (isLastItem(currentIndex)) {
            return `</${tagName}>${previousValue}<${tagName} id="${startId}">`;
         }

         return `</${tagName}>${previousValue}<${tagName}>`;
      }, "");
   }

   protected getEndBoundaryFromStack(stackOfParentElements: Array<StackItemNode>, endId: string): string {
      const isLastItem = (index: number) => {
         const limit = index + 1;
         return stackOfParentElements.length === limit;
      };

      return stackOfParentElements.reduce((previousValue, currentValue, currentIndex) => {
         const tagName = currentValue.nodeName;
         if (this.tagNameIs[this.toolType](tagName)) {
            return `<${tagName}>`;
         }

         if (currentValue.style !== "" && isLastItem(currentIndex)) {
            return `</${tagName}>${previousValue}<${tagName} style="${currentValue.style}" id="${endId}">`;
         }

         if (currentValue.style !== "") {
            return `</${tagName}>${previousValue}<${tagName} style="${currentValue.style}">`;
         }

         if (isLastItem(currentIndex)) {
            return `</${tagName}>${previousValue}<${tagName} id="${endId}">`;
         }

         return `</${tagName}>${previousValue}<${tagName}>`;
      }, "");
   }

   protected removeEmptyChildElements(element: Element): Element {
      const childNode = Array.from(element.childNodes);
      const filteredNodes = childNode
         .filter((node) => !this.isNodeWithEmptyText(<Element>node, true))
         .map((node) => <Node>node);

      const spanElement = document.createElement("SPAN");
      spanElement.append(...filteredNodes);
      console.log({ filteredNodes });
      return spanElement;
   }

   protected isNodeWithEmptyText(currentElement: Element, firstNode: boolean): boolean {
      if (!Boolean(currentElement) || (this.isText(currentElement) && firstNode)) {
         return false;
      }

      const { childNodes, firstElementChild } = currentElement;

      if (
         this.checkIs[this.toolType](currentElement) &&
         childNodes.length === 1 &&
         !this.hasSiblingNode(currentElement.firstChild)
      ) {
         return this.isNodeWithEmptyText(firstElementChild, false);
      }

      if (
         this.checkIs[this.toolType](currentElement) &&
         childNodes.length === 0 &&
         !this.hasSiblingNode(currentElement.firstChild)
      ) {
         return true;
      }

      if (!this.hasSiblingNode(currentElement) && childNodes.length === 0) {
         return true;
      }

      if (!this.isText(currentElement) && !this.hasSiblingNode(currentElement) && childNodes.length === 1) {
         return this.isNodeWithEmptyText(firstElementChild, false);
      }

      return (
         !this.isText(currentElement) && !this.hasSiblingNode(currentElement) && childNodes.length === 0 && !firstNode
      );
   }

   protected isNodeTypeChildWithoutSibling(node: Node): boolean {
      return (
         this.checkIs[this.toolType](node.parentNode) && !Boolean(node.nextSibling) && !Boolean(node.previousSibling)
      );
   }
}
